import { Attribute, LocatorStationsWrapper, Station } from "api"
import { AppType, useAppType } from "contexts/AppTypeProvider"
import { useRouter, useSearchParams } from "next/navigation"
import { useEffect, useRef } from "react"

interface UseUrlParamsProps {
    stations: LocatorStationsWrapper
    initialValueFilters?: Attribute[] | null
    setSelectedLocation: (station: Station) => void
    setSearchValue: (value: string) => void
    updateFilters: (filters: Attribute[]) => void
    resetFilters: () => void
}

export const useUrlParams = ({
    stations,
    initialValueFilters,
    setSelectedLocation,
    setSearchValue,
    updateFilters,
    resetFilters
}: UseUrlParamsProps) => {
    const { appType } = useAppType();
    const isPazCharge = appType === AppType.Charge;

    const searchParams = useSearchParams()
    const router = useRouter()
    const stationIdRef = useRef<string | null>(null)

    const getSelectedSiteFromUrl = () => {
        const siteId = searchParams.get("station")
        if (!siteId) {
            return
        }
        stationIdRef.current = siteId
        const siteFromUrl = stations.Body.body.stations.find(site => site.id === parseInt(siteId))

        if (!siteFromUrl) {
            return
        }
        if (isPazCharge) {
            // if the station from the url is not a charge station, redirect to service locator
            const isChargeStation = initialValueFilters?.some(filter =>
                siteFromUrl?.metaData?.some(meta => meta.id === filter.id)
            )
            if (!isChargeStation) {
                setTimeout(() => {
                    router.push("/service-locator/?station=" + siteId)
                }, 0)
                return
            }
        }
        setSelectedLocation(siteFromUrl)
    }

    const getSearchTermFromUrl = () => {
        const searchTermUrl = searchParams.get("search")
        if (!searchTermUrl) {
            return
        }
        setSearchValue(searchTermUrl)
    }

    const getSearchStringFromUrl = () => {
        const searchStringUrl = searchParams.get("searchString")
        if (!searchStringUrl) {
            return
        }
        // Decode the URL-encoded search string (handles Hebrew text)
        const decodedSearchString = decodeURIComponent(searchStringUrl)
        setSearchValue(decodedSearchString)

        // Remove searchString parameter from URL after setting it
        const url = new URL(window.location.href)
        const urlParams = new URLSearchParams(url.search)
        urlParams.delete('searchString')
        const newUrl = `${url.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}${url.hash}`
        window.history.replaceState(null, '', newUrl)

        // Check if searchString matches any station location
        const matchingStation = stations.Body.body.stations.find((station) => {
            const stationName = station.name || ''
            const cityName = station.city || ''

            return stationName.toLowerCase() === decodedSearchString.toLowerCase()
        })


        if (matchingStation) {
            setSelectedLocation(matchingStation)
        }
    }

    const getMetadataFromUrl = () => {
        const searchTermUrl = new URLSearchParams(window.location.search)
        const metadataUrl = searchTermUrl.get("metadata")
        if (!metadataUrl) {
            resetFilters()
            return
        }
        try {
            const attrs = JSON.parse(decodeURIComponent(metadataUrl))
            const metadataArray: Attribute[] = attrs.map((attr: any) => {
                return {
                    id: attr
                }
            })
            updateFilters(metadataArray)
        } catch (error) {
            return
        }
    }

    useEffect(() => {
        getSelectedSiteFromUrl()
        getSearchTermFromUrl()
        getSearchStringFromUrl()
        getMetadataFromUrl()
    }, [])

    return {
        stationIdRef
    }
}
